import React, { Component } from 'react';
import { connect } from 'react-redux';
import Auth from 'j-toker';
import { submit, isSubmitting, getFormValues } from 'redux-form';

import styled from 'styled-components';
import FlatButton from 'material-ui/FlatButton';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import { localize } from 'react-localize-redux';

import { getCurrentLocale } from 'helpers/i18n';
import { deepDiver } from 'helpers/utilities';
import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import { serviceRequestOptions } from 'helpers/VerbiageHelper';
import { formatPhone } from 'helpers/Formatter';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';
import { featureEnabled } from 'helpers/ToggleBot';

import RequestMaintenanceForm from './RequestMaintenanceForm';
import RequestMaintenanceFormIndustrial from './RequestMaintenanceFormIndustrial';
import SlideDownAnimation from 'components/shared/SlideDownAnimation';
import TicketConsentForm from './TicketConsentForm';
import ConsentToNoticeOfEntry from './ServiceRequests/ConsentToNoticeOfEntry';
import { getTranslatedString, getLandlordVerbiageOrTranslatedString } from 'helpers/i18n';

const FORM_NAME = 'new_tenant_ticket_form';

const StyledDiv = styled.div`
  background-color: ${colors.LIGHT_GRAY};
  border-radius: 8px;
  padding: 8px 8px 6px;
  margin: 12px 8px 0px 12px;
`;

const StyledDialogTitle = styled(DialogTitle)`
  margin-bottom: -20px;
`;

const StyledViewableByAll = styled.p`
  text-align: center;
  font-size: 12px;
  margin-right: 11px;
  margin-left: 11px;
  margin-bottom: 5px;
`;

const StyledImportantHeader = styled.h5`
  margin: 12px 0 5px;
  text-align: center;
  font-weight: bold;
`;

const StyledEmergencyNoteHeader = styled.p`
  text-align: center;
  font-size: 12px;
  margin-right: 11px;
  margin-left: 11px;
`;

class RequestMaintenanceDialog extends Component {
  state = {
    showMaintenanceWaiver: true,
  };

  isCommercialOrIndustrialProperty = () => {
    return ['industrial', 'commercial'].includes(
      deepDiver(this.props.tenantOverview, 'property_type')
    );
  };

  showPropertyMaintenanceWaiver = () => {
    const maintenanceWaiverDetails = deepDiver(
      this.props.tenancyInfo,
      'maintenance_waiver_details'
    );
    if (!maintenanceWaiverDetails) {
      return false;
    }
    return maintenanceWaiverDetails.active;
  };

  onRequestMaintenanceDialogClose = () => {
    this.props.toggleDialog();
    this.props.toggleOnMaintenanceWaiver();
  };

  toggleMaintenanceWaiver = () => {
    this.props.toggleOffMaintenanceWaiver();
  };

  render() {
    const { translate, verbiages } = this.props;

    const createMaintenanceRequestOptions = {
      serviceRequest: translate('createMaintenanceRequest'),
      requestForMaintenance: translate('createMaintenanceRequestRFM'),
    };

    let maintenanceTicketViewableByAll = this.props.translate('maintenanceTicketViewableByAll');

    if (
      deepDiver(verbiages, `residentPortal.maintenanceTicketViewableByAll.${getCurrentLocale()}`)
    ) {
      maintenanceTicketViewableByAll = deepDiver(
        verbiages,
        `residentPortal.maintenanceTicketViewableByAll.${getCurrentLocale()}`
      );
    }

    const showMaintenanceWaiver =
      !!this.showPropertyMaintenanceWaiver() && this.props.showMaintenanceWaiver;

    let stepVerbiage = translate('create');
    if (featureEnabled('ui.create_service_request.permission_to_enter') && this.props.step === 1) {
      stepVerbiage = translate('next');
    }
    return (
      <Dialog
        disableBackdropClick
        fullScreen={this.props.fullScreen}
        open={this.props.isOpen}
        onClose={this.props.toggleDialog}
        aria-labelledby="create-maintenance-ticket"
        classes={{
          paper: 'form-dialog',
        }}
      >
        {showMaintenanceWaiver && (
          <MaintenanceTicketWaiver
            tenancyInfo={this.props.tenancyInfo}
            greetingNameType={this.props.greetingNameType}
            toggleMaintenanceWaiver={this.toggleMaintenanceWaiver}
            onRequestMaintenanceDialogClose={this.onRequestMaintenanceDialogClose}
          />
        )}
        {!showMaintenanceWaiver && (
          <React.Fragment>
            <StyledDialogTitle id="create-maintenance-ticket">
              <h3 style={{ margin: '10px 0 5px', textAlign: 'center' }}>
                {serviceRequestOptions(createMaintenanceRequestOptions)}
                <b style={{ fontFamily: 'Circular-Black', color: AccentColor() }}>.</b>
              </h3>
              <StyledViewableByAll>{maintenanceTicketViewableByAll}</StyledViewableByAll>
              <StyledImportantHeader>
                {getTranslatedString('tenantPortal.requestMaintenanceDialog.important')}
              </StyledImportantHeader>
              <StyledEmergencyNoteHeader>
                {getTranslatedString('tenantPortal.requestMaintenanceDialog.emergencyNote')}
              </StyledEmergencyNoteHeader>

              {this._renderEmergencyBlurb()}
            </StyledDialogTitle>
            <DialogContent>
              <div
                style={{
                  borderTop: `5px solid ${AccentColor()}`,
                  marginTop: '5px',
                  paddingTop: '20px',
                }}
              >
                <SlideDownAnimation>{this._renderCommercialForm()}</SlideDownAnimation>
                <SlideDownAnimation>{this._renderNonCommercialForm()}</SlideDownAnimation>
                {featureEnabled('ui.residents.create_service_request.notice_of_entry_consent') &&
                this.props.step !== 1 ? (
                  <ConsentToNoticeOfEntry onSubmit={this.props.submitForm} />
                ) : (
                  <SlideDownAnimation>{this._renderTicketConsentForm()}</SlideDownAnimation>
                )}
              </div>
            </DialogContent>
            <DialogActions style={{ display: 'block', padding: '0 15px' }}>
              <FlatButton
                onClick={() => this.onRequestMaintenanceDialogClose()}
                id="close-button"
                style={{ color: AccentColor(), textTransform: 'uppercase' }}
              >
                {translate('close')}
              </FlatButton>
              <FlatButton
                variant="raised"
                onClick={() => this.props.submit(FORM_NAME)}
                disabled={this.props.submitting}
                id="submit-button"
                style={{
                  color: 'white',
                  backgroundColor: this.props.submitting ? colors.LIGHT_GRAY : AccentColor(),
                  textTransform: 'uppercase',
                }}
              >
                {stepVerbiage}
              </FlatButton>
            </DialogActions>
          </React.Fragment>
        )}
      </Dialog>
    );
  }

  _renderEmergencyBlurb = () => {
    if (!hostIsBoardwalk()) return null;
    const propertyNumber = this.props.tenancyInfo.property_phone_number;
    const formattedPropertyNumber = propertyNumber && formatPhone(propertyNumber);
    const landlordNumber = this.props.tenancyInfo.landlord_phone_number;
    const formattedLandlordNumber = landlordNumber && formatPhone(landlordNumber);

    if (!propertyNumber && !landlordNumber) return null;
    return (
      <StyledDiv>
        <p style={{ fontSize: '12px', margin: '0px', textAlign: 'center' }}>
          <span style={{ color: '#ff3434', fontWeight: '800' }}>
            {this.props.translate('tenantPortal.isThisEmergency')}{' '}
          </span>
          {propertyNumber && landlordNumber
            ? this.props.translate('tenantPortal.emergencySituationTwoPhones', {
                phoneNumber: propertyNumber,
                formattedNumber: formattedPropertyNumber,
                phoneNumberTwo: landlordNumber,
                formattedNumberTwo: formattedLandlordNumber,
              })
            : this.props.translate('tenantPortal.emergencySituationOnePhone', {
                phoneNumber: propertyNumber || landlordNumber,
                formattedNumber: formattedPropertyNumber || formattedLandlordNumber,
              })}
        </p>
      </StyledDiv>
    );
  };

  _renderNonCommercialForm = () => {
    if (this.props.step > 1 || this.isCommercialOrIndustrialProperty()) return;

    const propertyType = deepDiver(this.props.tenantOverview, 'property_type');
    const maintenanceCategories = this.props.maintenanceCategories.filter(
      (category) => category.property_type === propertyType
    );

    return (
      <RequestMaintenanceForm
        formName={FORM_NAME}
        maintenanceCategories={maintenanceCategories}
        maintenanceLocations={this.props.maintenanceLocations}
        onSubmit={this.props.submitForm}
        handleClose={this.props.toggleDialog}
        rooms={this.props.rooms}
        isOpen={this.props.isOpen}
        tenancyInfo={this.props.tenancyInfo}
      />
    );
  };

  _renderCommercialForm = () => {
    if (this.props.step > 1 || !this.isCommercialOrIndustrialProperty()) return;

    const propertyType = deepDiver(this.props.tenantOverview, 'property_type');
    const maintenanceCategories = this.props.maintenanceCategories.filter(
      (category) => category.property_type === propertyType
    );

    return (
      <RequestMaintenanceFormIndustrial
        formName={FORM_NAME}
        maintenanceCategories={maintenanceCategories}
        onSubmit={this.props.submitForm}
        handleClose={this.props.toggleDialog}
        rooms={this.props.rooms}
        isOpen={this.props.isOpen}
      />
    );
  };

  _renderTicketConsentForm = () => {
    if (this.props.step === 1) return;

    return (
      <TicketConsentForm
        formName={FORM_NAME}
        onSubmit={this.props.submitForm}
        landlease={deepDiver(this.props.tenantOverview, 'property_type') === 'mobile_home'}
        noticeEmailTurnedOn={this.props.noticeEmailTurnedOn}
      />
    );
  };
}

const ConnectedComponent = connect(
  (store) => ({
    maintenanceTicket: store.tenantMaintenance.tickets,
    tenantOverview: store.dashboard.tenantOverview,
    rooms: store.tenantMaintenance.rooms,
    submitting: isSubmitting(FORM_NAME)(store),
    currentFormData: getFormValues(FORM_NAME)(store),
    noticeEmailTurnedOn: store.userTypes.userPreferences.notice_by_email,
    tenancyInfo: store.tenantProfile.tenancyInfo,
    verbiages: store.userTypes.verbiages,
    maintenanceCategories: store.maintenanceReducer.maintenanceCategories,
    maintenanceLocations: store.maintenanceReducer.maintenanceLocations,
    greetingNameType: store.userTypes.greetingNameType,
  }),
  {
    submit,
    getFormValues,
  }
)(RequestMaintenanceDialog);
export default localize()(withMobileDialog()(ConnectedComponent));

const StyledWaiverHeader = styled.div`
  padding: 15px 24px;
  margin-bottom: -20px;
  font-size: 24px;
  font-weight: bold;
  text-align: left;
  color: ${colors.DARKEST_GRAY};
`;

const StyledWaiverTitle = styled.div`
  margin: 12px 0;
  font-size: 18px;
  font-weight: bold;
  text-align: left;
  color: ${colors.DARKEST_GRAY};
`;

const StyledWaiverSubTitle = styled.div`
  font-size: 16px;
  padding: 10px 24px;
`;

const StyledWaiverBody = styled.div`
  white-space: pre-wrap;
  font-size: 16px;
`;

const MaintenanceTicketWaiver = ({
  tenancyInfo,
  greetingNameType,
  toggleMaintenanceWaiver,
  onRequestMaintenanceDialogClose,
}) => (
  <React.Fragment>
    <div>
      <StyledWaiverHeader>
        {getLandlordVerbiageOrTranslatedString('nameGreeting', {
          name: Auth.user[greetingNameType],
        })}
      </StyledWaiverHeader>
      <StyledWaiverSubTitle>
        {getTranslatedString('tenantPortal.requestMaintenanceDialog.weHaveAMessage')}
      </StyledWaiverSubTitle>
    </div>
    <DialogContent>
      <div
        style={{
          borderTop: `5px solid ${AccentColor()}`,
          marginTop: '5px',
          paddingTop: '20px',
        }}
      >
        <StyledWaiverTitle>{tenancyInfo.property_maintenance_waiver.title}</StyledWaiverTitle>
        <StyledWaiverBody>{tenancyInfo.property_maintenance_waiver.body}</StyledWaiverBody>
      </div>
    </DialogContent>
    <DialogActions style={{ display: 'block', padding: '0 15px' }}>
      <FlatButton
        onClick={() => onRequestMaintenanceDialogClose()}
        id="close-button"
        style={{ color: AccentColor(), textTransform: 'uppercase' }}
      >
        {getTranslatedString('close')}
      </FlatButton>
      <FlatButton
        variant="raised"
        onClick={() => toggleMaintenanceWaiver()}
        id="submit-button"
        style={{
          color: 'white',
          backgroundColor: AccentColor(),
          textTransform: 'uppercase',
        }}
      >
        {getTranslatedString('continue')}
      </FlatButton>
    </DialogActions>
  </React.Fragment>
);
